/**
 * The card. The whole product, in 340 pixels of height.
 *
 * ── What it is trying to be ─────────────────────────────────────────────────
 *
 * §25's direction is Zillow crossed with Facebook Marketplace and CarGurus,
 * with a very light touch of HubSpot — warm, personal, fast, visual. Not a
 * table. Not enterprise CRM software. So the photograph is the largest thing
 * on it, the score and the money are readable at arm's length, and everything
 * that is not a decision is a chip.
 *
 * ── The one thing this card exists to say ───────────────────────────────────
 *
 * Non-negotiable principle 6: **landed cost matters more than asking price.**
 * A $7,500 trailer in Ceiba and a $9,200 one in Fajardo are not in the order
 * their stickers suggest once the barge is paid for, and the entire reason
 * this application exists is that no listing site anywhere will tell you that.
 *
 * So the card renders BOTH numbers, always, side by side — and whichever one
 * the current sort is ranking by is drawn large. Change the sort from ASKING
 * to LANDED and the weight moves across the card in front of you. With no
 * money sort the asking price leads, as it does on any listing site, and the
 * landed range sits beside it on its own tint so it is never the small print.
 * That is acceptance §17 #3 made visible rather than merely possible: the
 * contrast is a thing you watch happen, not a thing you have to infer from
 * two lists.
 *
 * ── OPEN ORIGINAL, and why a card fetches its own link ──────────────────────
 *
 * `GET /api/feed` deliberately never selects `listings.description`, and its
 * card projection carries no `source_url` either — §17.61 caps a 200-card page
 * at 512 KB and the projection is the reason it stays there. But §16 asks for
 * OPEN ORIGINAL on the card, so the link has to come from somewhere.
 *
 * It comes from `GET /api/listings?opportunityId=…`, memoised per id at module
 * scope behind a three-at-a-time gate, so paging back and forth never refetches
 * and twenty cards opening at once cannot bury the photographs they are next
 * to. A lead added through QUICK ADD genuinely has no link — that is a real
 * state, not a failure — and the button says so by being disabled with a
 * reason rather than by opening nothing.
 *
 * A FAILED fetch is never cached. "We could not ask" and "there is no link"
 * are different answers and only one of them is permanent.
 *
 * ── PASS takes a reason, and the reason list is the server's ────────────────
 *
 * §M and §N are built entirely on counting pass reasons, so a pass with no
 * reason is a feature that does not exist. `decision_reasons` is the authority
 * — it is admin-editable and carries `active` — so the sheet fetches it and
 * only falls back to `SEEDED_REASONS` when there is no signal, where the write
 * would be refused anyway and the refusal explains itself.
 *
 * ── §16, §18, §10.3 ─────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { get, patch, post } from '../lib/api'
import type { Locale } from '../lib/i18n'
import { Link, navigate } from '../lib/route'
import {
  DASH,
  TONES,
  ago,
  due,
  feet,
  money,
  range,
  score as scoreText,
  scoreTone,
  type Tone,
} from '../lib/format'
import {
  PASS_REASONS,
  availabilityLabel,
  canTransition,
  reasonLabel,
  stageLabel,
  stageRank,
  structureLabel,
  type Disposition,
  type Stage,
} from '../lib/domain'
import Icon, { type IconName } from './Icon'

/* ────────────────────────────────────────────────────────────────────────────
 * The wire shape
 *
 * A narrow mirror of `feed.ts`'s `FeedCard`. The client cannot import that
 * module — it pulls in `Env`, D1 and the whole Worker runtime — so the shape
 * is restated here once and imported by every screen that renders a card.
 * ────────────────────────────────────────────────────────────────────────── */

export interface FeedCard {
  id: string
  ref: string
  title: string
  stage: string
  lane: string | null
  disposition: string
  structureType: string | null
  year: number | null
  lengthFt: number | null
  askingPriceCents: number | null
  currency: string
  score: number | null
  confidence: number | null
  riskLevel: string
  landed: { low: number | null; expected: number | null; high: number | null } | null
  costConfidence: number | null
  municipality: string | null
  island: string | null
  availability: string | null
  mayBeGone: boolean
  isFavorite: boolean
  isFinalist: boolean
  dormant: boolean
  photoUrl: string | null
  discoveredAt: number | null
  postedAt: number | null
  lastActivityAt: number
  listingCount: number
  reasons: string[]
  nextAction: { key: string; text: string; dueOn: string | null; priority: number | null } | null
}

export interface FeedPage {
  cards: FeedCard[]
  cursor: string | null
  sort: string
}

/** Which number the current sort is ranking by. Drives the card's emphasis. */
export type Emphasis = 'score' | 'landed' | 'asking' | 'recent'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 *
 * Held here rather than in `src/lib/i18n.ts`, which another work unit owns in
 * this build. `ES` is `Record<CopyKey, string>`, so a missing translation is a
 * compile error in exactly the way the shared table's parity test achieves.
 * These keys move into `STRINGS` when the units merge.
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  scoreName: 'HOMESTEAD SCORE',
  unscored: 'NOT SCORED',
  unscoredWhy: 'Nothing has scored this yet.',
  asking: 'ASKING',
  landed: 'LANDED — ALL IN',
  noLanded: 'Not costed yet',
  noLandedWhy: 'No landed cost until it has been costed.',
  why: 'Why it scored highly',
  review: 'Review',
  contact: 'Contact',
  pass: 'Pass',
  openOriginal: 'Open original',
  noOriginal: 'No original link — this one was added by hand.',
  discovered: 'Discovered',
  favorite: 'Favourite',
  unfavorite: 'Remove from favourites',
  finalist: 'FINALIST',
  mayBeGone: 'MAY BE GONE',
  dormant: 'GONE QUIET',
  sources: 'listings',
  noPhoto: 'No photograph',
  passTitle: 'Why are you passing?',
  passWhy: 'Every pass is counted, and the counts are what teach the search what you do not want. It is the reason the picker is not optional.',
  passConfirm: 'Pass on this',
  cancel: 'Cancel',
  passed: 'Passed.',
  working: 'Working…',
  refused: 'That did not go through.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  scoreName: 'PUNTUACIÓN HOMESTEAD',
  unscored: 'SIN PUNTUAR',
  unscoredWhy: 'Todavía nada le ha puesto puntuación.',
  asking: 'PIDEN',
  landed: 'PUESTO AQUÍ — TODO',
  noLanded: 'Sin costo calculado',
  noLandedWhy: 'No hay costo puesto aquí hasta que se calcule.',
  why: 'Por qué puntuó alto',
  review: 'Revisar',
  contact: 'Contactar',
  pass: 'Descartar',
  openOriginal: 'Abrir original',
  noOriginal: 'Sin enlace original — este se añadió a mano.',
  discovered: 'Encontrado',
  favorite: 'Favorito',
  unfavorite: 'Quitar de favoritos',
  finalist: 'FINALISTA',
  mayBeGone: 'QUIZÁS YA NO ESTÁ',
  dormant: 'SIN MOVIMIENTO',
  sources: 'anuncios',
  noPhoto: 'Sin foto',
  passTitle: '¿Por qué lo descartas?',
  passWhy: 'Cada descarte se cuenta, y esas cuentas son las que le enseñan a la búsqueda lo que no quieres. Por eso el motivo no es opcional.',
  passConfirm: 'Descartar',
  cancel: 'Cancelar',
  passed: 'Descartado.',
  working: 'Trabajando…',
  refused: 'Eso no se pudo hacer.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/** The copy function every screen in this unit uses. Never memoised at module scope. */
export function cardCopy(locale: Locale): (key: CopyKey) => string {
  return (key) => COPY[locale][key] ?? EN[key]
}

/* ────────────────────────────────────────────────────────────────────────────
 * The original link
 * ────────────────────────────────────────────────────────────────────────── */

const urlCache = new Map<string, string | null>()
const urlPending = new Map<string, Promise<string | null>>()

/**
 * Three at a time.
 *
 * Twenty cards mounting at once would otherwise open twenty requests against
 * the same origin, competing with the twenty photographs that are the point of
 * the screen. Three keeps the link fetches behind the images without ever
 * stalling: the queue drains in well under a second.
 */
const MAX_PARALLEL = 3
let active = 0
const waiting: (() => void)[] = []

function acquire(): Promise<void> {
  if (active < MAX_PARALLEL) {
    active += 1
    return Promise.resolve()
  }
  return new Promise<void>((resolve) => {
    waiting.push(() => {
      active += 1
      resolve()
    })
  })
}

function release(): void {
  active -= 1
  const next = waiting.shift()
  if (next) next()
}

/**
 * The first listing under this opportunity that has a link, or `null`.
 *
 * A REFUSAL or a network failure is deliberately NOT cached: "there is no
 * link" is permanent and "we could not ask" is not, and caching the second as
 * the first disables a button for the rest of the session over one dropped
 * request in a tunnel.
 */
export async function sourceUrlOf(id: string): Promise<string | null> {
  const held = urlCache.get(id)
  if (held !== undefined) return held
  const already = urlPending.get(id)
  if (already) return already

  const run = (async (): Promise<string | null> => {
    await acquire()
    try {
      const body = await get<{ listings: { source_url: string | null }[] }>(
        `/api/listings?opportunityId=${encodeURIComponent(id)}`,
      )
      const found = body.listings.find((row) => typeof row.source_url === 'string' && row.source_url)
      const url = found?.source_url ?? null
      urlCache.set(id, url)
      return url
    } catch {
      return null
    } finally {
      release()
      urlPending.delete(id)
    }
  })()

  urlPending.set(id, run)
  return run
}

/** `undefined` while it is still being asked for; `null` when there is none. */
function useSourceUrl(id: string): string | null | undefined {
  const [url, setUrl] = useState<string | null | undefined>(() => urlCache.get(id))

  useEffect(() => {
    const held = urlCache.get(id)
    if (held !== undefined) {
      setUrl(held)
      return
    }
    let live = true
    setUrl(undefined)
    void sourceUrlOf(id).then((found) => {
      if (live) setUrl(found)
    })
    return () => {
      live = false
    }
  }, [id])

  return url
}

/* ────────────────────────────────────────────────────────────────────────────
 * Writes
 * ────────────────────────────────────────────────────────────────────────── */

export interface TransitionResult {
  ok: boolean
  code?: string
  message?: string
  stage?: string
  disposition?: string
}

/**
 * §5's contract: an illegal transition is a **200 with `ok: false`**, never a
 * throw and never a 4xx. So the only thing that can reject here is the network,
 * and that is turned into the same shape rather than into an exception the
 * caller would have to remember to catch.
 */
export async function transitionOpportunity(
  id: string,
  body: Record<string, unknown>,
): Promise<TransitionResult> {
  try {
    return await post<TransitionResult>(`/api/opportunities/${encodeURIComponent(id)}/transition`, body)
  } catch (error) {
    return { ok: false, code: 'network', message: error instanceof Error ? error.message : undefined }
  }
}

export async function setFavorite(id: string, isFavorite: boolean): Promise<boolean> {
  try {
    await patch<{ ok: boolean }>(`/api/opportunities/${encodeURIComponent(id)}`, { isFavorite })
    return true
  } catch {
    return false
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Decision reasons
 * ────────────────────────────────────────────────────────────────────────── */

export interface ReasonRow {
  code: string
  kind: string
  label_en: string
  label_es: string
  sort_order: number
  active: number
}

const reasonCache = new Map<string, ReasonRow[]>()

/**
 * The active reasons of one kind.
 *
 * The TABLE is the authority — a reason deactivated in the console must stop
 * being offered, and a constant compiled into the bundle cannot know that. The
 * seeded codes are the fallback for no signal only, where the write is going
 * to be refused regardless and the refusal says why.
 */
export async function reasonsOf(kind: string): Promise<ReasonRow[]> {
  const held = reasonCache.get(kind)
  if (held) return held
  try {
    const body = await get<{ reasons: ReasonRow[] }>(`/api/decision-reasons?kind=${encodeURIComponent(kind)}`)
    const rows = (body.reasons ?? []).filter((row) => row.active === 1)
    if (rows.length) reasonCache.set(kind, rows)
    return rows
  } catch {
    return []
  }
}

export function reasonText(row: ReasonRow, locale: Locale): string {
  const label = locale === 'es-PR' ? row.label_es : row.label_en
  return label || reasonLabel(row.code, locale)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Style tokens
 *
 * Inline, against `var(--…)`, for the same reason the detail panels are:
 * `index.css` belongs to another unit in this build and a class name written
 * here would have nothing behind it. Every colour is a token, so both themes
 * work without a second definition.
 * ────────────────────────────────────────────────────────────────────────── */

export const CARD_S = {
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 14,
    boxShadow: 'var(--elev-1)',
  } as CSSProperties,

  photoWrap: {
    position: 'relative',
    display: 'block',
    width: '100%',
    aspectRatio: '16 / 9',
    background: 'var(--score-weak-bg)',
    overflow: 'hidden',
  } as CSSProperties,

  photo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } as CSSProperties,

  body: { padding: '12px 12px 14px' } as CSSProperties,

  title: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } as CSSProperties,

  place: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: 'var(--muted)',
    fontSize: 13,
    marginTop: 4,
  } as CSSProperties,

  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 } as CSSProperties,

  note: { fontSize: 13, lineHeight: 1.45, color: 'var(--muted)', margin: 0 } as CSSProperties,

  button: {
    flex: '1 1 0',
    minHeight: 46,
    padding: '0 10px',
    borderRadius: 10,
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    textDecoration: 'none',
  } as CSSProperties,
} as const

export function toneStyle(tone: Tone, outlined = false): CSSProperties {
  return outlined
    ? {
        color: tone.fg,
        background: 'transparent',
        border: `1px dashed ${tone.line === 'transparent' ? tone.fg : tone.line}`,
      }
    : { color: tone.fg, background: tone.bg, border: `1px solid ${tone.line}` }
}

export function Chip({
  tone,
  icon,
  title,
  children,
}: {
  tone: Tone
  icon?: IconName
  title?: string
  children: ReactNode
}) {
  return (
    <span
      title={title}
      style={{
        ...toneStyle(tone),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        borderRadius: 999,
        padding: '3px 9px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  )
}

/*
 * The two overlay colours are NOT theme tokens, and that is deliberate: they
 * sit on top of a photograph, whose brightness has nothing to do with the
 * viewer's colour scheme. A `var(--surface)` scrim would be unreadable on half
 * the images in either theme.
 */
const OVERLAY_BG = 'rgba(12, 13, 16, 0.66)'
const OVERLAY_FG = '#ffffff'

/* ────────────────────────────────────────────────────────────────────────────
 * The card
 * ────────────────────────────────────────────────────────────────────────── */

export interface CardProps {
  card: FeedCard
  locale: Locale
  /** Which figure the current sort ranks by. The large one on the card. */
  emphasis?: Emphasis
  /** Its place in the current ordering, 1-based. Shown only when ranking money. */
  rank?: number
  /** The row changed under us — passed, favourited, moved. */
  onChanged?: (id: string, change: 'passed' | 'favorite' | 'stage') => void
  /** Something to say out loud. The screen owns the snackbar. */
  onSay?: (message: string) => void
}

export default function OpportunityCard({ card, locale, emphasis = 'score', rank, onChanged, onSay }: CardProps) {
  const t = useMemo(() => cardCopy(locale), [locale])
  const url = useSourceUrl(card.id)
  const [isFavorite, setIsFavorite] = useState(card.isFavorite)
  const [passing, setPassing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [broken, setBroken] = useState(false)

  useEffect(() => {
    setIsFavorite(card.isFavorite)
  }, [card.isFavorite])

  const href = `/o/${encodeURIComponent(card.id)}`

  const landedTriple =
    card.landed && card.landed.low !== null && card.landed.high !== null
      ? { low: card.landed.low, expected: card.landed.expected ?? card.landed.low, high: card.landed.high }
      : null

  const landedText = landedTriple
    ? range(landedTriple, locale)
    : card.landed?.expected != null
      ? money(card.landed.expected, locale)
      : null

  /*
   * CONTACT is always live, because it always does the thing it says: it opens
   * the screen the seller's channels are on. What it does to the PIPELINE is
   * conditional — it advances the stage only when `contact_seller` is FORWARD
   * of where this is now, and only when `canTransition` (the very function the
   * handler runs, CLAUDE.md rule 14) accepts the move.
   *
   * The forward test is the important half. `canTransition` permits backward
   * moves before the purchase wall, so without it, pressing CONTACT on a lead
   * at `negotiating` would rewind it to `contact_seller` and write a decision
   * row saying so — a button quietly undoing three stages of work.
   */
  const state = {
    stage: card.stage as Stage,
    disposition: card.disposition as Disposition,
    isFinalist: card.isFinalist,
  }
  const contactOutcome = canTransition(state, { stage: 'contact_seller' })
  const advancesOnContact = contactOutcome.ok && stageRank(card.stage) < stageRank('contact_seller')

  const toggleFavorite = useCallback(async () => {
    const next = !isFavorite
    setIsFavorite(next)
    if (!(await setFavorite(card.id, next))) {
      setIsFavorite(!next)
      onSay?.(t('refused'))
      return
    }
    onChanged?.(card.id, 'favorite')
  }, [card.id, isFavorite, onChanged, onSay, t])

  const review = useCallback(() => {
    /*
     * Opening a lead IS reviewing it, so the stage follows the person rather
     * than waiting for them to remember to move it. Only ever `new →
     * reviewing`: anything further along is already past this, and a card
     * action must never walk a stage backwards.
     */
    if (card.stage === 'new') void transitionOpportunity(card.id, { stage: 'reviewing' })
    navigate(href)
  }, [card.id, card.stage, href])

  const contact = useCallback(async () => {
    setBusy(true)
    /*
     * The stage moves here and the screen moves to the detail, because the
     * detail is where the seller's channels live — `sellers.ts` is the only
     * producer of a `tel:` / `sms:` / `wa.me` href and the only place an
     * interaction is logged. This button is the intent; that screen is the act.
     */
    if (advancesOnContact) {
      await transitionOpportunity(card.id, { stage: 'contact_seller' })
      onChanged?.(card.id, 'stage')
    }
    setBusy(false)
    navigate(href)
  }, [advancesOnContact, card.id, href, onChanged])

  return (
    <article style={CARD_S.card} data-opportunity={card.id}>
      <Link to={href} style={CARD_S.photoWrap} aria-label={card.title}>
        {card.photoUrl && !broken ? (
          <img
            src={card.photoUrl}
            alt=""
            loading="lazy"
            decoding="async"
            style={CARD_S.photo}
            onError={() => setBroken(true)}
          />
        ) : (
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: 'var(--muted)',
              fontSize: 12,
            }}
          >
            <Icon name="camera" size={28} />
            {t('noPhoto')}
          </span>
        )}

        {/* The score, top left, at the size it is actually read from. */}
        <span
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            ...toneStyle(card.score === null ? TONES.quiet : scoreTone(card.score)),
            borderRadius: 12,
            padding: '5px 10px 6px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            lineHeight: 1,
            boxShadow: 'var(--elev-1)',
          }}
          title={card.confidence === null ? undefined : `${card.confidence}%`}
        >
          <b style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {card.score === null ? DASH : scoreText(card.score)}
          </b>
          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.08em', marginTop: 3 }}>
            {card.score === null ? t('unscored') : t('scoreName')}
          </span>
        </span>

        {/* Discovery recency, top right — §16 asks for it by name. */}
        <span
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: OVERLAY_BG,
            color: OVERLAY_FG,
            borderRadius: 999,
            padding: '4px 9px',
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {ago(card.discoveredAt, locale)}
        </span>

        {(card.mayBeGone || card.dormant || card.isFinalist || rank !== undefined) && (
          <span
            style={{
              position: 'absolute',
              left: 10,
              bottom: 10,
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              maxWidth: 'calc(100% - 20px)',
            }}
          >
            {rank !== undefined && (
              <Chip tone={TONES.brand} icon="chart">
                #{rank}
              </Chip>
            )}
            {card.isFinalist && (
              <Chip tone={TONES.strong} icon="star">
                {t('finalist')}
              </Chip>
            )}
            {card.mayBeGone && (
              <Chip tone={TONES.danger} icon="alert">
                {t('mayBeGone')}
              </Chip>
            )}
            {card.dormant && (
              <Chip tone={TONES.quiet} icon="clock">
                {t('dormant')}
              </Chip>
            )}
          </span>
        )}
      </Link>

      <div style={CARD_S.body}>
        <Link to={href} style={{ color: 'inherit', textDecoration: 'none' }}>
          <h2 style={CARD_S.title}>{card.title}</h2>
        </Link>

        <p style={CARD_S.place}>
          <Icon name="pin" size={14} />
          {[card.municipality, card.island].filter(Boolean).join(' · ') || DASH}
        </p>

        <MoneyBlock
          asking={card.askingPriceCents}
          landedText={landedText}
          emphasis={emphasis}
          locale={locale}
          t={t}
        />

        <Attributes card={card} locale={locale} t={t} />

        {card.reasons.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                color: 'var(--muted)',
              }}
            >
              {t('why').toUpperCase()}
            </p>
            <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0 }}>
              {card.reasons.slice(0, 3).map((reason) => (
                <li
                  key={reason}
                  style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '2px 0', fontSize: 13.5 }}
                >
                  <span style={{ color: 'var(--score-strong)', flex: '0 0 auto', marginTop: 2 }}>
                    <Icon name="check" size={13} />
                  </span>
                  <span style={{ lineHeight: 1.4 }}>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {card.nextAction && (
          <p
            style={{
              ...CARD_S.note,
              marginTop: 10,
              display: 'flex',
              gap: 6,
              alignItems: 'flex-start',
              color: 'var(--ink)',
            }}
          >
            <span style={{ color: 'var(--brand-strong)', flex: '0 0 auto', marginTop: 1 }}>
              <Icon name="alert" size={14} />
            </span>
            <span>
              {card.nextAction.text}
              {card.nextAction.dueOn && (
                <span style={{ color: 'var(--muted)' }}> · {due(card.nextAction.dueOn, locale)}</span>
              )}
            </span>
          </p>
        )}

        {/* Four actions, two rows. Four across at 390px is 86px each, which is
            a row of truncated words and a mis-tap; two rows is neither. */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            onClick={review}
            style={{
              ...CARD_S.button,
              flex: '1.3 1 0',
              background: 'var(--brand)',
              color: 'var(--brand-ink)',
              borderColor: 'var(--brand)',
            }}
          >
            <Icon name="eye" size={17} />
            {t('review')}
          </button>
          <button
            type="button"
            onClick={() => void contact()}
            disabled={busy}
            style={CARD_S.button}
          >
            <Icon name="phone" size={17} />
            {busy ? t('working') : t('contact')}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => setPassing(true)}
            style={{ ...CARD_S.button, color: 'var(--muted)' }}
          >
            <Icon name="close" size={16} />
            {t('pass')}
          </button>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...CARD_S.button, color: 'var(--muted)' }}
            >
              <Icon name="globe" size={16} />
              {t('openOriginal')}
            </a>
          ) : (
            <button
              type="button"
              disabled
              title={url === null ? t('noOriginal') : undefined}
              style={{ ...CARD_S.button, color: 'var(--muted)', opacity: 0.45, cursor: 'default' }}
            >
              <Icon name="globe" size={16} />
              {t('openOriginal')}
            </button>
          )}
          <button
            type="button"
            onClick={() => void toggleFavorite()}
            aria-pressed={isFavorite}
            aria-label={isFavorite ? t('unfavorite') : t('favorite')}
            style={{
              ...CARD_S.button,
              flex: '0 0 auto',
              width: 50,
              color: isFavorite ? 'var(--danger-solid)' : 'var(--muted)',
              borderColor: isFavorite ? 'var(--danger-solid)' : 'var(--line)',
            }}
          >
            <Icon name="heart" size={18} />
          </button>
        </div>
      </div>

      {passing && (
        <PassSheet
          id={card.id}
          title={card.title}
          locale={locale}
          onClose={() => setPassing(false)}
          onPassed={() => {
            setPassing(false)
            onSay?.(t('passed'))
            onChanged?.(card.id, 'passed')
          }}
          onRefused={(message) => {
            setPassing(false)
            onSay?.(message)
          }}
        />
      )}
    </article>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The two numbers
 * ────────────────────────────────────────────────────────────────────────── */

function MoneyBlock({
  asking,
  landedText,
  emphasis,
  locale,
  t,
}: {
  asking: number | null
  landedText: string | null
  emphasis: Emphasis
  locale: Locale
  t: (key: CopyKey) => string
}) {
  const leadLanded = emphasis === 'landed'
  const lead: CSSProperties = { fontSize: 25, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }
  const second: CSSProperties = { fontSize: 16, fontWeight: 700, lineHeight: 1.2 }
  const label: CSSProperties = {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: '0.08em',
    color: 'var(--muted)',
    display: 'block',
    marginBottom: 2,
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 10,
        marginTop: 10,
        paddingTop: 10,
        borderTop: '1px solid var(--line)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <span style={label}>{t('asking')}</span>
        <span style={leadLanded ? second : lead}>{money(asking, locale)}</span>
      </div>

      <div
        style={{
          minWidth: 0,
          textAlign: 'right',
          ...(landedText
            ? {
                color: 'var(--money)',
                background: 'var(--money-bg)',
                borderRadius: 10,
                padding: '5px 9px',
                marginRight: -2,
              }
            : {}),
        }}
      >
        <span style={{ ...label, color: landedText ? 'var(--money)' : 'var(--muted)', opacity: 0.85 }}>
          {t('landed')}
        </span>
        {landedText ? (
          <span style={leadLanded ? lead : second}>{landedText}</span>
        ) : (
          <span style={{ ...second, color: 'var(--muted)', fontWeight: 600 }} title={t('noLandedWhy')}>
            {t('noLanded')}
          </span>
        )}
      </div>
    </div>
  )
}

function Attributes({
  card,
  locale,
  t,
}: {
  card: FeedCard
  locale: Locale
  t: (key: CopyKey) => string
}) {
  /* At most four. §10.3 caps them because a fifth chip pushes the actions off
     the first screenful, and the actions are the point of the card. */
  const chips: { key: string; text: string; tone: Tone; icon?: IconName }[] = []

  if (card.structureType) {
    chips.push({ key: 'kind', text: structureLabel(card.structureType, locale), tone: TONES.quiet, icon: 'home' })
  }
  if (card.year !== null) chips.push({ key: 'year', text: String(card.year), tone: TONES.quiet })
  if (card.lengthFt !== null) chips.push({ key: 'len', text: feet(card.lengthFt, locale), tone: TONES.quiet })
  if (card.availability && card.availability !== 'active') {
    chips.push({
      key: 'avail',
      text: availabilityLabel(card.availability, locale),
      tone: TONES.warn,
      icon: 'alert',
    })
  }
  if (chips.length < 4 && card.listingCount > 1) {
    chips.push({ key: 'srcs', text: `${card.listingCount} ${t('sources')}`, tone: TONES.quiet })
  }
  if (chips.length < 4) {
    chips.push({ key: 'stage', text: stageLabel(card.stage, locale), tone: TONES.brand, icon: 'route' })
  }

  return (
    <div style={CARD_S.chipRow}>
      {chips.slice(0, 4).map((chip) => (
        <Chip key={chip.key} tone={chip.tone} icon={chip.icon}>
          {chip.text}
        </Chip>
      ))}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The pass sheet
 * ────────────────────────────────────────────────────────────────────────── */

export function PassSheet({
  id,
  title,
  locale,
  onClose,
  onPassed,
  onRefused,
}: {
  id: string
  title: string
  locale: Locale
  onClose: () => void
  onPassed: () => void
  onRefused: (message: string) => void
}) {
  const t = useMemo(() => cardCopy(locale), [locale])
  const [rows, setRows] = useState<ReasonRow[] | null>(null)
  const [chosen, setChosen] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const first = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    let live = true
    void reasonsOf('pass').then((found) => {
      if (!live) return
      setRows(
        found.length
          ? found
          : /* No signal. The seeded codes, so the sheet is never empty — the
               write itself is validated against the table regardless. */
            PASS_REASONS.map((code, index) => ({
              code,
              kind: 'pass',
              label_en: reasonLabel(code, 'en'),
              label_es: reasonLabel(code, 'es-PR'),
              sort_order: index,
              active: 1,
            })),
      )
    })
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    first.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const confirm = useCallback(async () => {
    if (!chosen) return
    setBusy(true)
    const result = await transitionOpportunity(id, { disposition: 'passed', reasonCode: chosen })
    setBusy(false)
    if (result.ok) onPassed()
    else onRefused(result.message ?? t('refused'))
  }, [chosen, id, onPassed, onRefused, t])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('passTitle')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(8, 9, 12, 0.5)',
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          maxHeight: '82vh',
          overflowY: 'auto',
          background: 'var(--bg)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          border: '1px solid var(--line)',
          padding: '16px 14px calc(16px + env(safe-area-inset-bottom))',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{t('passTitle')}</h2>
        <p style={{ ...CARD_S.note, margin: '4px 0 0' }}>{title}</p>
        <p style={{ ...CARD_S.note, margin: '8px 0 0' }}>{t('passWhy')}</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '14px 0 4px' }}>
          {(rows ?? []).map((row, index) => {
            const on = chosen === row.code
            return (
              <button
                key={row.code}
                ref={index === 0 ? first : undefined}
                type="button"
                onClick={() => setChosen(row.code)}
                aria-pressed={on}
                style={{
                  minHeight: 44,
                  padding: '0 12px',
                  borderRadius: 999,
                  border: `1px solid ${on ? 'var(--brand)' : 'var(--line)'}`,
                  background: on ? 'var(--brand-soft)' : 'var(--surface)',
                  color: on ? 'var(--brand-strong)' : 'var(--ink)',
                  font: 'inherit',
                  fontSize: 14,
                  fontWeight: on ? 800 : 600,
                  cursor: 'pointer',
                }}
              >
                {reasonText(row, locale)}
              </button>
            )
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onClose} style={{ ...CARD_S.button }}>
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!chosen || busy}
            style={{
              ...CARD_S.button,
              flex: '1.4 1 0',
              background: chosen ? 'var(--danger-solid)' : 'var(--surface)',
              borderColor: chosen ? 'var(--danger-solid)' : 'var(--line)',
              color: chosen ? '#fff' : 'var(--muted)',
              opacity: chosen ? 1 : 0.7,
            }}
          >
            {busy ? t('working') : t('passConfirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The compact row — the board and the picker
 *
 * The same information, ranked the same way, at a third of the height. It is a
 * separate component rather than a `dense` prop on the card because the two
 * have different jobs: the card is read, this is scanned.
 * ────────────────────────────────────────────────────────────────────────── */

export function MiniCard({
  card,
  locale,
  selected,
  onSelect,
  draggable,
  onDragStart,
}: {
  card: FeedCard
  locale: Locale
  /** Present makes this a choice rather than a link. */
  selected?: boolean
  onSelect?: (id: string) => void
  draggable?: boolean
  onDragStart?: (id: string) => void
}) {
  const t = useMemo(() => cardCopy(locale), [locale])
  const landed =
    card.landed && card.landed.expected !== null ? money(card.landed.expected, locale) : t('noLanded')

  const inner = (
    <>
      <span
        style={{
          flex: '0 0 auto',
          width: 62,
          height: 62,
          borderRadius: 10,
          overflow: 'hidden',
          background: 'var(--score-weak-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--muted)',
        }}
      >
        {card.photoUrl ? (
          <img
            src={card.photoUrl}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon name="camera" size={18} />
        )}
      </span>

      <span style={{ flex: '1 1 auto', minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {card.title}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
          {[card.municipality, card.island].filter(Boolean).join(' · ') || DASH}
        </span>
        <span style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 12.5, alignItems: 'baseline' }}>
          <b>{money(card.askingPriceCents, locale)}</b>
          <span style={{ color: 'var(--money)', fontWeight: 700 }}>{landed}</span>
        </span>
      </span>

      <span
        style={{
          flex: '0 0 auto',
          ...toneStyle(card.score === null ? TONES.quiet : scoreTone(card.score)),
          borderRadius: 8,
          padding: '4px 7px',
          fontSize: 15,
          fontWeight: 800,
          alignSelf: 'flex-start',
        }}
      >
        {card.score === null ? DASH : scoreText(card.score)}
      </span>
    </>
  )

  const shell: CSSProperties = {
    display: 'flex',
    gap: 10,
    alignItems: 'stretch',
    width: '100%',
    textAlign: 'left',
    padding: 8,
    marginBottom: 8,
    borderRadius: 12,
    border: `1px solid ${selected ? 'var(--brand)' : 'var(--line)'}`,
    background: selected ? 'var(--brand-soft)' : 'var(--surface)',
    color: 'var(--ink)',
    textDecoration: 'none',
    font: 'inherit',
    cursor: 'pointer',
  }

  if (onSelect) {
    return (
      <button type="button" onClick={() => onSelect(card.id)} aria-pressed={!!selected} style={shell}>
        {inner}
      </button>
    )
  }

  return (
    <Link
      to={`/o/${encodeURIComponent(card.id)}`}
      style={shell}
      draggable={draggable}
      onDragStart={(event) => {
        if (!draggable) return
        event.dataTransfer.setData('text/plain', card.id)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart?.(card.id)
      }}
    >
      {inner}
    </Link>
  )
}
